import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check auth status on mount
  const checkAuth = useCallback(async () => {
    try {
      const userData = await authAPI.me();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await authAPI.login(email, password);
    
    // Check if MFA is required
    if (response.requires_mfa) {
      // Don't set user/authenticated yet - MFA required
      return {
        requires_mfa: true,
        user_id: response.user_id,
        mfa_method: response.mfa_method,
        message: response.message
      };
    }
    
    setUser(response.user);
    setIsAuthenticated(true);
    return response;
  };
  
  // Complete login after MFA verification
  const completeMfaLogin = async (loginResponse) => {
    setUser(loginResponse.user);
    setIsAuthenticated(true);
    return loginResponse;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register(name, email, password);
    setUser(response.user);
    setIsAuthenticated(true);
    return response;
  };

  const googleLogin = async (sessionId) => {
    const response = await authAPI.googleSession(sessionId);
    setUser(response.user);
    setIsAuthenticated(true);
    return response;
  };

  const logout = async () => {
    try {
      const response = await authAPI.logout();
      
      // Clear impersonation data from localStorage
      localStorage.removeItem('impersonation');
      
      // Check if we returned to admin session (from impersonation)
      if (response?.returned_to_admin) {
        // Reload to apply admin session
        window.location.href = '/admin';
        return;
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear impersonation data on logout
      localStorage.removeItem('impersonation');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    login,
    completeMfaLogin,
    register,
    googleLogin,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
